import React, { useEffect, useState } from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'
import { VictoryPie } from 'victory'
import { CircleLabelNumber, ColorDash } from '../../components/ReportPieChartDecorations'
import {
  REPORT_MOTIVE_CARD_COLORS,
  REPORT_MOTIVE_CARD_SX
} from './constants/reportMotiveCard'
import { useReportPieChartMetrics } from '../helpers/useReportPieChartMetrics'

const ReportMotiveCard = ({ reportMotives, selectedTab, title, compact = false }) => {
  const { isMobileLayout, chartSize, innerRadius, radius, legendMaxHeight, gridGap } =
    useReportPieChartMetrics({ compact })
  const totalValue = reportMotives.reduce((total, motive) => total + motive.value, 0)

  const [numberValue, setNumberValue] = useState(totalValue)
  const [hoverIndex, setHoverIndex] = useState(-1)

  const buildReportMotives = () =>
    reportMotives.map((motive) => ({
      x: motive.text,
      y: motive.value
    }))

  const sortReportMotives = () => {
    return [...reportMotives].sort((a, b) => {
      return a.value < b.value ? 1 : -1
    })
  }

  const findOriginalIndex = (motiveTxt) => {
    return reportMotives.findIndex((motive) => {
      return motive.text === motiveTxt
    })
  }

  useEffect(() => {
    setNumberValue(totalValue)
  }, [selectedTab, totalValue])

  return (
    <Card elevation={0} sx={REPORT_MOTIVE_CARD_SX}>
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: isMobileLayout ? 2 : 1.75,
          '&:last-child': { pb: isMobileLayout ? 2 : 1.75 }
        }}
      >
        {title && (
          <Typography
            sx={{
              fontSize: isMobileLayout ? 14 : 13,
              fontWeight: 700,
              lineHeight: 1.25,
              color: '#334155',
              mb: isMobileLayout ? 2 : 1.25
            }}
          >
            {title}
          </Typography>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: isMobileLayout
              ? '1fr'
              : `${chartSize}px minmax(0, 1fr)`,
            gap: gridGap,
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: chartSize,
              height: chartSize,
              mx: 'auto'
            }}
          >
            <VictoryPie
              width={chartSize}
              height={chartSize}
              style={{ labels: { fill: 'black' } }}
              padAngle={2}
              colorScale={REPORT_MOTIVE_CARD_COLORS}
              innerRadius={innerRadius}
              radius={radius}
              labels={() => ''}
              data={buildReportMotives()}
              events={[
                {
                  target: 'data',
                  eventHandlers: {
                    onMouseOver: () => {
                      return [
                        {
                          target: 'labels',
                          mutation: (props) => {
                            setNumberValue(reportMotives[props.index].value)
                            setHoverIndex(props.index)
                            return null
                          }
                        }
                      ]
                    },
                    onMouseLeave: () => {
                      return [
                        {
                          target: 'labels',
                          mutation: () => {
                            setNumberValue(totalValue)
                            setHoverIndex(-1)
                            return null
                          }
                        }
                      ]
                    }
                  }
                }
              ]}
            />
            <CircleLabelNumber number={numberValue} />
          </Box>

          <Box
            sx={{
              minHeight: 0,
              maxHeight: legendMaxHeight,
              overflowY: 'auto',
              pr: 0.5
            }}
          >
            {sortReportMotives().map((motive, index) => {
              const originalIndex = findOriginalIndex(motive.text)

              return (
                <Box
                  key={index}
                  onMouseOver={() => {
                    setNumberValue(reportMotives[originalIndex].value)
                    setHoverIndex(originalIndex)
                  }}
                  onMouseLeave={() => {
                    setNumberValue(totalValue)
                    setHoverIndex(-1)
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 0.5,
                    py: 0.35,
                    borderRadius: 1,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  <ColorDash
                    palette={REPORT_MOTIVE_CARD_COLORS}
                    originalIndex={originalIndex}
                    selectedIndex={hoverIndex}
                  />

                  <Typography
                    sx={{
                      minWidth: 34,
                      fontSize: isMobileLayout ? 13 : 12,
                      fontWeight: 700,
                      color: '#111827',
                      textAlign: 'right'
                    }}
                  >
                    {motive.value}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: isMobileLayout ? 13 : 12,
                      color: '#0F172A',
                      lineHeight: 1.35,
                      minWidth: 0,
                      flex: 1,
                      wordBreak: 'break-word'
                    }}
                  >
                    {motive.text}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default ReportMotiveCard
